/**
 * The lifecycle of every record that moves through states, as tables of allowed transitions.
 *
 * Each table is the single place a state change is judged legal. The database layer calls `can`
 * before writing a new state, and fulfillment uses it to refuse a pharmacy event that would jump
 * an order backwards (FUL-011). Keeping them as data, not branching code, is what lets a test walk
 * every pair and prove the refusals.
 */

/** A transition table: for each state, the states it may move to. */
export type Machine<S extends string> = Readonly<Record<S, readonly S[]>>;

export function can<S extends string>(machine: Machine<S>, from: S, to: S): boolean {
  return machine[from].includes(to);
}

/** A state with no way out. */
export function isTerminal<S extends string>(machine: Machine<S>, state: S): boolean {
  return machine[state].length === 0;
}

export type FormulaVersionState = "draft" | "in_review" | "approved" | "declined" | "superseded";

/**
 * A version is editable only as a draft. After review it is fixed (DAT-002), and the only change
 * left is being superseded by a newer version.
 */
export const formulaVersion: Machine<FormulaVersionState> = {
  draft: ["in_review", "superseded"],
  in_review: ["approved", "declined", "superseded"],
  approved: ["superseded"],
  declined: [],
  superseded: [],
};

export type ClinicalReviewState = "waiting" | "in_progress" | "approved" | "edited" | "declined";

export const clinicalReview: Machine<ClinicalReviewState> = {
  waiting: ["in_progress"],
  in_progress: ["approved", "edited", "declined", "waiting"],
  approved: [],
  edited: [],
  declined: [],
};

export type ScriptState = "drafted" | "released" | "exhausted" | "expired" | "voided";

/** A script reaches the pharmacy only by being released, which happens on payment (CLI-030). */
export const script: Machine<ScriptState> = {
  drafted: ["released", "voided", "expired"],
  released: ["exhausted", "expired", "voided"],
  exhausted: [],
  expired: [],
  voided: [],
};

export type OrderState = "pending_payment" | "paid" | "released" | "cancelled" | "refunded";

export const order: Machine<OrderState> = {
  pending_payment: ["paid", "cancelled"],
  paid: ["released", "refunded", "cancelled"],
  released: ["refunded"],
  cancelled: [],
  refunded: [],
};

export type SubscriptionState = "active" | "paused" | "past_due" | "cancelled";

export const subscription: Machine<SubscriptionState> = {
  active: ["paused", "past_due", "cancelled"],
  paused: ["active", "cancelled"],
  past_due: ["active", "cancelled"],
  /* ACC-023: a cancelled customer restarts with one action. */
  cancelled: ["active"],
};

export type CheckInState = "sent" | "answered" | "lapsed";

/** A check-in is answered once, or lapses when the renewal day passes with no reply (ACC-013). */
export const checkIn: Machine<CheckInState> = {
  sent: ["answered", "lapsed"],
  answered: [],
  lapsed: [],
};

export type PharmacyOrderState =
  | "queued"
  | "submitted"
  | "accepted"
  | "in_production"
  | "quality_check"
  | "shipped"
  | "delivered"
  | "rejected"
  | "on_hold"
  | "delivery_exception"
  | "cancelled";

/**
 * The pharmacy order table from the fulfillment spec. `on_hold` lists every state it can return
 * to. Which one is legal depends on where the order was held from, which `canResume` checks.
 */
export const pharmacyOrder: Machine<PharmacyOrderState> = {
  queued: ["submitted", "cancelled"],
  submitted: ["accepted", "rejected"],
  accepted: ["in_production", "on_hold", "cancelled"],
  in_production: ["quality_check", "on_hold"],
  quality_check: ["shipped", "on_hold"],
  shipped: ["delivered", "delivery_exception"],
  delivered: [],
  rejected: ["queued", "cancelled"],
  on_hold: ["accepted", "in_production", "quality_check", "cancelled"],
  delivery_exception: ["shipped", "delivered", "cancelled"],
  cancelled: [],
};

/** Whether an order held from `heldFrom` may move to `to`: back where it was, or cancelled. */
export function canResume(heldFrom: PharmacyOrderState, to: PharmacyOrderState): boolean {
  return to === "cancelled" || to === heldFrom;
}

export function isPharmacyOrderState(value: string): value is PharmacyOrderState {
  return Object.hasOwn(pharmacyOrder, value);
}

/** The five statuses a customer sees (FUL-030), so internal steps never leak into the account. */
export type CustomerOrderStatus = "received" | "preparing" | "shipped" | "delivered" | "attention";

export function customerStatus(state: PharmacyOrderState): CustomerOrderStatus {
  switch (state) {
    case "queued":
    case "submitted":
    case "accepted":
      return "received";
    case "in_production":
    case "quality_check":
      return "preparing";
    case "shipped":
      return "shipped";
    case "delivered":
      return "delivered";
    case "rejected":
    case "on_hold":
    case "delivery_exception":
    case "cancelled":
      return "attention";
  }
}
